import { SafeStorage } from './safe-storage';

/**
 * The one way the app reloads itself.
 *
 * The upload loop lives in this page's memory and nothing on the server knows
 * it exists, so a reload ends it silently. About two dozen screens used to call
 * `window.location.reload()` on their own, none aware of an upload running in
 * the background (a minimized batch outlives the dialog that started it, so the
 * old "no dialog open" check protected nothing). Everything now goes through
 * here, which does two things: refuses to kill a live upload, and records why
 * the page reloaded so the next page can say so.
 */

const REASON_KEY = 'appReloadReason';

interface UploadGuard {
  isLive: () => boolean;
  report: (event: string, props?: Record<string, any>) => void;
}

let guard: UploadGuard | null = null;
let deferred: { reason: string } | null = null;
let reloadStartedAt: number | null = null;

/** Called once by UploadBannerService, which owns the upload heartbeat. */
export function registerUploadGuard(g: UploadGuard): void {
  guard = g;
}

/** True while this window is running an upload loop. */
export function isUploadLive(): boolean {
  try {
    return !!guard?.isLive();
  } catch {
    return false;
  }
}

/**
 * Reload the page, unless an upload is running in it.
 *
 * Deferred reloads are not dropped: the callers wanted fresh state, and the
 * upload ending is the next safe moment to give it to them (`flushDeferredReload`).
 * Pass `force` only where the reload IS the user's explicit intent or the page
 * would be wrong to keep running (sign-in recovery, an identity switch, a tap
 * on a refresh control).
 */
export function reloadApp(reason: string, opts: { force?: boolean } = {}): void {
  if (!opts.force && isUploadLive()) {
    // First reason wins for the report; the reload itself is the same either way.
    const first = !deferred;
    deferred = deferred ?? { reason };
    if (first) {
      guard?.report('app_reload_deferred', { reason });
    }
    return;
  }
  deferred = null;
  // A reload already under way (cache clearing and a service-worker handoff can
  // take seconds) must not be started a second time: two `app_update` reloads
  // landed four seconds apart in testing.
  if (reloadStartedAt !== null && Date.now() - reloadStartedAt < 10000) {
    return;
  }
  reloadStartedAt = Date.now();
  try {
    SafeStorage.setItem(REASON_KEY, JSON.stringify({ reason, at: Date.now(), forced: !!opts.force }));
  } catch {
    // Telemetry about the reload must never stop the reload.
  }
  window.location.reload();
}

/** Called when the upload loop ends. Performs a reload that was held back. */
export function flushDeferredReload(): void {
  if (!deferred || isUploadLive()) {
    return;
  }
  const { reason } = deferred;
  // Let the upload's own finishing work (gallery refresh, done notice) settle first.
  setTimeout(() => {
    if (deferred && !isUploadLive()) {
      reloadApp(`${reason}:after_upload`);
    }
  }, 1500);
}

/** The reason recorded by the previous page, if it reloaded through `reloadApp`. Read once. */
export function takeReloadReason(): { reason: string; at: number; forced: boolean } | null {
  try {
    const raw = SafeStorage.getItem(REASON_KEY);
    if (!raw) {
      return null;
    }
    SafeStorage.removeItem(REASON_KEY);
    const parsed = JSON.parse(raw);
    return typeof parsed?.reason === 'string' ? parsed : null;
  } catch {
    return null;
  }
}
